<template>
  <div class="wecom-login-container">
    <div class="text-center mb-4">
      <h3 class="text-lg font-semibold text-gray-700"></h3>
    </div>

    <!-- QR Code Display -->
    <div class="qr-code-container flex justify-center mb-4">
      <div class="w-48 h-48 flex items-center justify-center">
        <div v-if="isLoading" class="text-center">
          <div class="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
          <p class="text-sm text-gray-500 mt-1">正在初始化...</p>
        </div>
        <div v-else-if="showHostedBridgeEntry" class="text-center px-4">
          <p class="text-sm text-blueGray-600 leading-6">
            当前域名不能直接展示企业微信登录二维码，请点击下方按钮前往授权登录页。
          </p>
          <button
            type="button"
            class="mt-4 px-4 py-2 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
            @click="goToHostedLogin"
          >
            前往企业微信登录
          </button>
        </div>
        <div v-if="qrError" class="text-center">
          <p class="text-red-500 text-sm">{{ qrError }}</p>
          <button
            @click="createLoginPanel"
            class="mt-2 px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
          >
            重新初始化
          </button>
        </div>
      </div>
    </div>

    <!-- Status Messages -->
    <div v-if="statusMessage" class="text-center mb-4">
      <p :class="statusClass">{{ statusMessage }}</p>
    </div>

  </div>
</template>

<script>
import { createWWLoginPanel } from '@wecom/jssdk';

const WECOM_APP_ID = process.env.VUE_APP_WECOM_APP_ID || 'ww29cb4167ee240553';
const WECOM_AGENT_ID = process.env.VUE_APP_WECOM_AGENT_ID || '1000055';
const WECOM_CALLBACK_URL =
  process.env.VUE_APP_WECOM_CALLBACK_URL || 'https://admin.52choujiang.cn/auth/wxauth';
const WECOM_LOGIN_BRIDGE_URL =
  process.env.VUE_APP_WECOM_LOGIN_BRIDGE_URL || 'https://admin.52choujiang.cn/auth/login';
const WECOM_TEST_REDIRECT_KEY = 'wecom_test_redirect';
const WECOM_BRIDGE_REDIRECT_KEY = 'wecom_bridge_redirect';
const DEFAULT_TEST_CALLBACK_URL =
  process.env.VUE_APP_WECOM_TEST_CALLBACK_URL || '';

function getCurrentCallbackUrl() {
  return `${window.location.origin}/auth/wxauth`;
}

function normalizeCallbackUrl(value) {
  if (!value) {
    return '';
  }

  try {
    const callbackUrl = new URL(value);
    if (!['http:', 'https:'].includes(callbackUrl.protocol)) {
      return '';
    }
    if (callbackUrl.pathname !== '/auth/wxauth') {
      return '';
    }
    return callbackUrl.toString();
  } catch (error) {
    return '';
  }
}

function getDefaultTestCallbackUrl() {
  return normalizeCallbackUrl(DEFAULT_TEST_CALLBACK_URL) || getCurrentCallbackUrl();
}

function getStoredTestRedirectTarget() {
  const storedValue = window.localStorage.getItem(WECOM_TEST_REDIRECT_KEY);
  if (!storedValue) {
    return '';
  }

  const defaultTestCallbackUrl = getDefaultTestCallbackUrl();
  if (['1', 'true', 'test'].includes(storedValue.toLowerCase())) {
    return defaultTestCallbackUrl;
  }

  const storedTarget = normalizeCallbackUrl(storedValue);
  if (storedTarget && storedTarget !== defaultTestCallbackUrl) {
    window.localStorage.setItem(WECOM_TEST_REDIRECT_KEY, defaultTestCallbackUrl);
    return defaultTestCallbackUrl;
  }
  return storedTarget;
}

function shouldUseHostedLoginBridge() {
  return window.location.origin !== new URL(WECOM_CALLBACK_URL).origin;
}

function buildHostedBridgeUrl() {
  const bridgeUrl = new URL(WECOM_LOGIN_BRIDGE_URL);
  bridgeUrl.searchParams.set('redirect', getStoredTestRedirectTarget() || getCurrentCallbackUrl());
  return bridgeUrl.toString();
}

function buildWeComState(redirectTarget) {
  if (!redirectTarget) {
    return 'loginState';
  }
  return `loginState|${encodeURIComponent(redirectTarget)}`;
}

function getBridgeRedirectTarget() {
  const redirectFromQuery = new URLSearchParams(window.location.search).get('redirect');
  return normalizeCallbackUrl(redirectFromQuery)
    || getStoredTestRedirectTarget()
    || normalizeCallbackUrl(window.sessionStorage.getItem(WECOM_BRIDGE_REDIRECT_KEY))
    || getCurrentCallbackUrl();
}

function saveBridgeRedirectTarget(redirectTarget) {
  if (redirectTarget && redirectTarget !== getCurrentCallbackUrl()) {
    window.sessionStorage.setItem(WECOM_BRIDGE_REDIRECT_KEY, redirectTarget);
  }
}

function buildWeComRedirectUri() {
  const currentCallbackUrl = getCurrentCallbackUrl();
  const redirectTarget = getBridgeRedirectTarget();
  saveBridgeRedirectTarget(redirectTarget);

  if (currentCallbackUrl === WECOM_CALLBACK_URL) {
    const separator = WECOM_CALLBACK_URL.includes('?') ? '&' : '?';
    return redirectTarget === currentCallbackUrl
      ? WECOM_CALLBACK_URL
      : `${WECOM_CALLBACK_URL}${separator}redirect=${encodeURIComponent(redirectTarget)}`;
  }

  const separator = WECOM_CALLBACK_URL.includes('?') ? '&' : '?';
  return `${WECOM_CALLBACK_URL}${separator}redirect=${encodeURIComponent(currentCallbackUrl)}`;
}

export default {
  name: 'WeComLogin',
  data() {
    return {
      isLoading: true,
      qrError: null,
      isPolling: false,
      statusMessage: '',
      qrCodeUrl: '',
      pollingInterval: null,
      qrState: 'STATE',
      wecomLoginPanel: null,
      showHostedBridgeEntry: false,
    };
  },
  computed: {
    statusClass() {
      return {
        'text-green-500': this.statusMessage.includes('成功'),
        'text-blue-500': this.statusMessage.includes('等待') || this.statusMessage.includes('请'),
        'text-red-500': this.statusMessage.includes('失败') || this.statusMessage.includes('错误'),
        'text-sm': true
      };
    }
  },
  async mounted() {
    await this.createLoginPanel();
  },
  methods: {
    async createLoginPanel() {
      this.isLoading = true;
      this.qrError = null;
      this.showHostedBridgeEntry = false;
      this.statusMessage = '正在初始化登录组件...';

      try {
        if (shouldUseHostedLoginBridge()) {
          this.statusMessage = '当前环境需要通过授权登录页完成企业微信登录';
          this.showHostedBridgeEntry = true;
          return;
        }

        const redirectUri = buildWeComRedirectUri();
        const oauthState = buildWeComState(getBridgeRedirectTarget());
        // 创建企业微信登录面板
        this.wecomLoginPanel = createWWLoginPanel({
          el: this.$el.querySelector('.qr-code-container'),
          params: {
            login_type: 'CorpApp',
            appid: WECOM_APP_ID,
            agentid: WECOM_AGENT_ID,
            redirect_uri: redirectUri,
            state: oauthState,
            redirect_type: 'callback'
          },
          onCheckWeComLogin: ({ isWeComLogin }) => {
            console.log('检查企业微信登录状态:', isWeComLogin);
          },
          onLoginSuccess({ code }) {
            const separator = redirectUri.includes('?') ? '&' : '?';
            const redirectUrl = `${redirectUri}${separator}code=${encodeURIComponent(code)}&state=${encodeURIComponent(oauthState)}`;
            window.location.href = redirectUrl;
          },
          onLoginFail: (err) => {
            console.error('登录失败:', err);
            this.qrError = '登录失败，请重试';
            this.statusMessage = '登录失败';
          },
          onOpenInWecom: () => {
            console.log('成功打开企业微信客户端');
          }
        });
        this.statusMessage = '请使用企业微信扫描二维码登录';

      } catch (error) {
        console.error('创建登录面板失败:', error);
        this.qrError = '初始化登录组件失败，请刷新页面重试';
        this.statusMessage = '组件初始化失败';
      } finally {
        this.isLoading = false;
      }
    },
    goToHostedLogin() {
      const bridgeUrl = buildHostedBridgeUrl();
      window.location.href = bridgeUrl;
    },

    buildWeComAuthUrl() {
      const redirectUri = buildWeComRedirectUri();
      const oauthState = buildWeComState(getBridgeRedirectTarget());
      // 企业微信OAuth2授权URL
      const params = new URLSearchParams({
        appid: WECOM_APP_ID,
        agentid: WECOM_AGENT_ID,
        redirect_uri: redirectUri,
        response_type: 'code',
        scope: 'snsapi_userinfo',
        state: oauthState
      });

      return `https://open.weixin.qq.com/connect/oauth2/authorize?${params.toString()}#wechat_redirect`;
    },
    onQrCodeLoaded() {
      this.isLoading = false;
    },

    onQrCodeError() {
      this.qrError = '二维码加载失败';
      this.isLoading = false;
      this.statusMessage = '二维码加载失败，请重试';
    }
  }
};
</script>

<style scoped>
.wecom-login-container {
  max-width: 300px;
  margin: 0 auto;
}

.qr-code-container {
  min-height: 250px;
}

.instructions {
  margin-top: 1rem;
}
</style>
